/**
 * Read more toggle for sections/collection-seo-text.liquid, and a cap on the
 * Findter app's infinite scroll on pages that show the text.
 *
 * The section's CSS already holds a collapsible text at its collapsed height,
 * so nothing moves when this script runs. It only marks the text as
 * overflowing - which shows the fade and the button - when the text is taller
 * than that, and re-checks when the width changes.
 *
 * Infinite scroll: the app's <fdt-infinite-scroll> loads the next page when it
 * comes within 200px of the viewport, by moving `window.fdtPage.current` on and
 * dispatching `findter-load-more` on the document. The text sits just below the
 * grid, so scrolling down to read it kept loading products that pushed it away
 * again, up to the whole collection. After `data-auto-pages` automatic loads,
 * the event is stopped before the app sees it, the page counter is put back,
 * and the list switches to the app's own "Vis mere" button (rendered in the
 * page but kept hidden in infinite scroll mode). A visitor's click on that
 * button always loads. A filter or sort change starts again from page 1, which
 * turns infinite scroll back on.
 */
class CollectionSeoText extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.onLoadMore = this.onLoadMore.bind(this);
    this.onProductsRender = this.onProductsRender.bind(this);
    // Capture on window runs before the app's listeners on the document
    window.addEventListener('click', this.onClick, true);
    window.addEventListener('findter-load-more', this.onLoadMore, true);
    window.addEventListener('findter-products-render', this.onProductsRender, true);

    this.content = this.querySelector('.seo-text__content');
    this.button = this.querySelector('.seo-text__toggle');
    if (!this.content || !this.button) return;

    this.button.addEventListener('click', () => this.setExpanded(!this.hasAttribute('expanded')));
    // A link cut off by the collapse can still take keyboard focus; show it instead of scrolling inside the box
    this.content.addEventListener('focusin', () => this.hasAttribute('overflowing') && this.setExpanded(true));

    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.content);
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.onClick, true);
    window.removeEventListener('findter-load-more', this.onLoadMore, true);
    window.removeEventListener('findter-products-render', this.onProductsRender, true);
    this.observer?.disconnect();
  }

  get autoPages() {
    const pages = Number.parseInt(this.dataset.autoPages, 10);
    return Number.isNaN(pages) ? Infinity : pages;
  }

  onClick(event) {
    // The app's button dispatches `findter-load-more` while handling the click, so this flag is still set then.
    // Not a microtask: those run between the listeners of a visitor's click, before the app's handler.
    this.visitorClick = event.isTrusted && Boolean(event.target.closest?.('load-more-btn'));
    if (this.visitorClick) setTimeout(() => (this.visitorClick = false));
  }

  onLoadMore(event) {
    const page = Number(window.fdtPage?.current);
    if (this.visitorClick || !page || page <= this.autoPages + 1) return;

    event.stopImmediatePropagation();
    window.fdtPage.current = page - 1;
    window.fdtPage.next = page;
    this.setManual(true);
  }

  onProductsRender() {
    if (Number(window.fdtPage?.current) <= this.autoPages) this.setManual(false);
  }

  setManual(manual) {
    const infiniteScroll = document.querySelector('fdt-infinite-scroll');
    const loadMore = document.querySelector('fdt-load-more');
    if (!infiniteScroll || !loadMore) return;

    // Hidden, the infinite scroll element no longer intersects, so it stops firing; it also repeats the
    // "Du har set ... produkter" line of the button's wrapper
    infiniteScroll.style.display = manual ? 'none' : '';
    loadMore.classList.toggle('visibility-hidden', !manual);
    loadMore.querySelector('load-more-btn')?.classList.remove('triggered', 'hidden');
  }

  measure() {
    if (this.hasAttribute('expanded')) return;

    const overflowing = this.content.scrollHeight > this.content.clientHeight + 1;
    this.toggleAttribute('overflowing', overflowing);
    this.button.hidden = !overflowing;
  }

  setExpanded(expanded) {
    this.toggleAttribute('expanded', expanded);
    this.button.setAttribute('aria-expanded', String(expanded));
    this.button.textContent = expanded ? this.dataset.lessLabel : this.dataset.moreLabel;

    if (!expanded) {
      this.measure();
      // Collapsing a long text from its end would leave the visitor far below it
      if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
    }
  }
}

if (!customElements.get('collection-seo-text')) {
  customElements.define('collection-seo-text', CollectionSeoText);
}
